import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import {
    globalLoader,
    handleServerValidations,
} from "../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { Update, Details } from "../../services/pujaCategory.services";

const EditPujaCategory = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [existingImage, setExistingImage] = useState(null);
    const [initialData, setInitialData] = useState({
        title: "",
        description: "",
        color: "#f5f0e8",
        status: 1,
        type: "puja",
        chart_type: "single",
        image: null,
        sort_order: 0,
    });

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Categories", url: "/admin/puja-category/list/1" },
        { title: t("btn_edit"), url: "" },
    ];

    useEffect(() => {
        if (id) {
            globalLoader(true);
            Details(id)
                .then((response) => {
                    if (response.success) {
                        const data = response.data;
                        setInitialData({
                            title: data.title || "",
                            description: data.description || "",
                            color: data.color || "#f5f0e8",
                            status: data.status,
                            type: data.type || "puja",
                            chart_type: data.chart_type || "single",
                            image: null,
                            sort_order: data.sort_order || 0,
                        });
                        if (data.image) {
                            const imgUrl = data.image.startsWith("http")
                                ? data.image
                                : `${process.env.REACT_APP_API_URL || ""}/${data.image}`;
                            setExistingImage(imgUrl);
                        }
                    }
                    globalLoader(false);
                })
                .catch((error) => {
                    console.log("error fetching details", error);
                    globalLoader(false);
                });
        }
    }, [id]);

    const validationSchema = Yup.object().shape({
        title: Yup.string().trim().required("Title is required"),
        description: Yup.string().trim().required("Description is required"),
        type: Yup.string().required("Type is required"),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialData,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            globalLoader(true);
            setSubmitted(true);

            const formData = new FormData();
            formData.append("id", id);
            formData.append("title", values.title);
            formData.append("description", values.description);
            formData.append("color", values.color || "#ffffff");
            formData.append("status", values.status);
            formData.append("type", values.type);
            if (values.type === "expertise") {
                formData.append("chart_type", values.chart_type || "single");
            }
            formData.append("sort_order", values.sort_order);
            if (values.image) {
                formData.append("image", values.image);
            }

            Update(formData)
                .then((response) => {
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setTimeout(() => {
                            navigate(`/admin/puja-category/list/1`);
                        }, 1000);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setSubmitted(false);
                    }
                })
                .catch((error) => {
                    console.log("error ====> ", error);
                    globalLoader(false);
                    setSubmitted(false);
                });
        },
    });

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            formik.setFieldValue("image", file);
            const reader = new FileReader();
            reader.onload = (ev) => setPreviewImage(ev.target.result);
            reader.readAsDataURL(file);
        }
    };

    const currentImageSrc = previewImage || existingImage;

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <h6 className="main-content-label mb-3">Edit Category</h6>
                        <form onSubmit={formik.handleSubmit}>
                            <div className="row row-sm">
                                {/* Title */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="title" className="text-left d-flex">
                                        Title :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="title"
                                        id="title"
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter category title"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                    />
                                    {formik.errors.title && formik.touched.title && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.title}
                                        </span>
                                    )}
                                </div>

                                {/* Status */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="status" className="text-left d-flex">
                                        Status :
                                    </label>
                                    <select
                                        name="status"
                                        id="status"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.status}
                                    >
                                        <option value={1}>{t("Active")}</option>
                                        <option value={0}>{t("Inactive")}</option>
                                    </select>
                                </div>

                                {/* Type */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="type" className="text-left d-flex">
                                        Type :<span className="requirestar">*</span>
                                    </label>
                                    <select
                                        name="type"
                                        id="type"
                                        className="form-control text-capitalize"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.type}
                                    >
                                        <option value="puja">Puja</option>
                                        <option value="expertise">Expertise</option>
                                    </select>
                                    {formik.errors.type && formik.touched.type && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.type}
                                        </span>
                                    )}
                                </div>

                                {/* Chart Type - Only when Type is Expertise */}
                                {formik.values.type === "expertise" && (
                                    <div className="col-lg-6 form-group">
                                        <label htmlFor="chart_type" className="text-left d-flex">
                                            Chart Type :<span className="requirestar">*</span>
                                        </label>
                                        <select
                                            name="chart_type"
                                            id="chart_type"
                                            className="form-control text-capitalize"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.chart_type}
                                        >
                                            <option value="single">Single</option>
                                            <option value="dual">Dual</option>
                                        </select>
                                    </div>
                                )}

                                {/* Sort Order */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="sort_order" className="text-left d-flex">
                                        Sort Order / Priority :
                                    </label>
                                    <input
                                        name="sort_order"
                                        id="sort_order"
                                        type="number"
                                        min="0"
                                        className="form-control"
                                        placeholder="Enter sort order (e.g. 1, 2, 3)"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.sort_order}
                                    />
                                </div>

                                {/* Image */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="image" className="text-left d-flex">
                                        Icon / Image :
                                    </label>
                                    <input
                                        name="image"
                                        id="image"
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                    {currentImageSrc && (
                                        <div className="mt-2">
                                            <img
                                                src={currentImageSrc}
                                                alt="Current"
                                                style={{
                                                    width: "80px",
                                                    height: "80px",
                                                    objectFit: "cover",
                                                    borderRadius: "10px",
                                                    border: "1px solid #dee2e6",
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Description */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="description" className="text-left d-flex">
                                        Description :<span className="requirestar">*</span>
                                    </label>
                                    <textarea
                                        name="description"
                                        id="description"
                                        className="form-control"
                                        rows="4"
                                        placeholder="Enter category description"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.description}
                                    ></textarea>
                                    {formik.errors.description && formik.touched.description && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.description}
                                        </span>
                                    )}
                                </div>

                                {/* Live Preview Card */}
                                {/* {(formik.values.title || formik.values.description) && (
                                    <div className="col-lg-12 form-group">
                                        <label className="text-left d-flex">Live Preview :</label>
                                        <div
                                            style={{
                                                display: "inline-flex",
                                                flexDirection: "column",
                                                padding: "18px",
                                                borderRadius: "16px",
                                                backgroundColor: formik.values.color || "#f5f0e8",
                                                minWidth: "160px",
                                                maxWidth: "200px",
                                                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                                            }}
                                        >
                                            {currentImageSrc && (
                                                <img
                                                    src={currentImageSrc}
                                                    alt="icon"
                                                    style={{
                                                        width: "40px",
                                                        height: "40px",
                                                        objectFit: "cover",
                                                        borderRadius: "8px",
                                                        marginBottom: "10px",
                                                    }}
                                                />
                                            )}
                                            <strong style={{ fontSize: "14px", marginBottom: "6px" }}>
                                                {formik.values.title || "Category Title"}
                                            </strong>
                                            <span style={{ fontSize: "12px", color: "#666" }}>
                                                {formik.values.description || "Description here"}
                                            </span>
                                        </div>
                                    </div>
                                )} */}

                                <div className="col-md-12">
                                    <button
                                        className="btn btn-main-primary signbtn mr-2"
                                        type="submit"
                                        disabled={submitted}
                                    >
                                        <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                        {t("btn_update")}
                                    </button>
                                    <button
                                        className="btn ripple btn-secondary"
                                        type="button"
                                        onClick={() => navigate(-1)}
                                    >
                                        <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                        {t("btn_cancel")}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditPujaCategory;
